import type {
  BillingInterval,
  ObPlanId,
  PlanOption,
} from '../../../gql/graphql'

import { BillingIntervalValues } from '../types'
import type { PlanCalculation, PlanInfo } from '../types'
import type { QueryDiscount } from './discountCalculations'
import { calculateTierPrice } from './pricingCalculations'
import type { usePlanSelectorQuery } from '../hooks/usePlanSelectorQuery'

/**
 * Extended plan with calculations
 */
export interface ExtendedPlanOption extends PlanOption {
  calculations: PlanCalculation
  appliedDiscounts?: QueryDiscount[]
}
/**
 * Base type from the GraphQL query
 */
type BasePlanSelectorData = NonNullable<
  ReturnType<typeof usePlanSelectorQuery>['data']
>

/**
 * Extended plan selector data with calculated plans
 * Explicitly preserves all fields from the base type while extending availablePlans
 */
export interface ExtendedPlanSelectorData extends Omit<
  BasePlanSelectorData,
  'availablePlans'
> {
  availablePlans: ExtendedPlanOption[]
  // Explicitly include subscription to help TypeScript
  subscription: BasePlanSelectorData['subscription']
}

/**
 * Find the current plan from available plans
 */
export function findCurrentPlan(
  availablePlans: PlanOption[],
  currentPlanId: ObPlanId,
  billingCycle: BillingInterval,
): PlanOption {
  const plan = availablePlans.find(
    (p) => p.planId === currentPlanId && p.planInterval === billingCycle,
  )

  if (!plan) {
    throw new Error(
      `Current plan not found: ${currentPlanId} with interval ${billingCycle}`,
    )
  }

  return plan
}

/**
 * Filter plans by billing cycle
 */
export function filterPlansByBillingCycle(
  plans: PlanOption[],
  billingCycle: BillingInterval,
): PlanOption[] {
  const targetInterval =
    billingCycle === BillingIntervalValues.month
      ? BillingIntervalValues.month
      : BillingIntervalValues.year

  return plans.filter((plan) => plan.planInterval === targetInterval)
}

/**
 * Create PlanInfo object from plan and selection data
 */
export function createPlanInfo(
  plan: PlanOption,
  channelCount: number,
  billingCycle: BillingInterval,
  calculations: Record<ObPlanId, PlanCalculation>,
): PlanInfo {
  return {
    planId: plan.planId,
    planName: plan.planName,
    channelCount,
    billingCyclePrice: calculations[plan.planId]?.totalPrice || 0,
    billingCycle,
    pricePerChannel: calculations[plan.planId]?.pricePerChannel || 0,
  }
}

/**
 * Create current plan info based on subscription data
 */
export function createCurrentPlanInfo(
  plan: PlanOption,
  subscriptionQuantity: number,
): PlanInfo {
  const { totalPrice, pricePerChannel } = calculateTierPrice(
    plan.pricing,
    subscriptionQuantity,
  )

  return {
    planId: plan.planId,
    planName: plan.planName,
    channelCount: subscriptionQuantity,
    billingCyclePrice: totalPrice,
    billingCycle: plan.planInterval,
    pricePerChannel,
  }
}

/**
 * Finds the current pricing tier index for a given channel count
 */
export function findCurrentTierIndex(
  channelCount: number,
  sortedTiers: NonNullable<ExtendedPlanOption['pricing']>,
): number {
  const index = sortedTiers.findIndex((tier) => channelCount <= tier.upperBound)
  return index === -1 ? sortedTiers.length - 1 : index
}

/**
 * Gets volume discount message based on pricing tiers
 */
export function getVolumeDiscountMessage(
  channelCount: number,
  planPricing?: ExtendedPlanOption['pricing'],
): string | null {
  if (!planPricing?.length || planPricing.length <= 1 || channelCount <= 0) {
    return null
  }

  // Ensure tiers are sorted by upperBound for reliable tier detection
  const sortedTiers = planPricing.toSorted(
    (a, b) => a.upperBound - b.upperBound,
  )
  const currentTierIndex = findCurrentTierIndex(channelCount, sortedTiers)

  if (currentTierIndex > 0) {
    const previousTier = sortedTiers[currentTierIndex - 1]
    const threshold = previousTier.upperBound + 1
    return `${threshold}+ channels discount applied`
  }

  return null
}
